/**
 * The app shell — BRAND.md §5: 224px navy sidebar (light logo banner, product
 * switcher, nav, user footer), white topbar with breadcrumb + page title + one
 * primary action, Light Grey canvas.
 *
 * The product switcher is rendered but inert -- there is no cross-product
 * navigation from here yet. The user footer is real as of Phase 5, and says
 * which of the two modes the server is in rather than pretending there is
 * always an account.
 */

import type { ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import type { AuthState, ObligationTypeInUse } from '../api'
import { typeLabel } from '../due-list'
import { TimerBar } from './TimeCapture'

interface AppShellProps {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
  /** Breadcrumb above the title. */
  eyebrow?: string
  title: ReactNode
  subtitle?: ReactNode
  /** The single primary action for this view, if it has one. */
  action?: ReactNode
  children: ReactNode
}

export function AppShell({
  obligationTypes,
  auth,
  eyebrow,
  title,
  subtitle,
  action,
  children,
}: AppShellProps) {
  const signedIn = auth.mode === 'cognito' && auth.user !== null
  const manager = signedIn && auth.user?.role === 'manager'
  return (
    <div className="flex min-h-screen bg-eq-grey">
      <aside className="flex w-[224px] shrink-0 flex-col bg-eq-navy">
        {/* Light lockup on navy only — BRAND.md §8. The banner masthead is the
            one intentional exception to the clear-space rule. */}
        <div className="px-4 py-5">
          <img
            src="/eq/eq-horizontal-light-tight.png"
            alt="Equilibrium"
            className="h-8 w-auto"
          />
        </div>

        <div className="border-y border-[rgba(255,255,255,.08)] px-4 py-3">
          <div className="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-[rgba(255,255,255,.6)]">
            Product
          </div>
          <div className="mt-1 font-heading text-[15px] font-bold text-white">
            Equilibrium Practice
          </div>
        </div>

        {/* T&B-1 PR 5 (spec, "Sidebar regrouping"): four groups instead of one
            flat list -- Work, Clients, Time, Admin. Manager-only items stay
            hidden from staff, as User access always was: the API refuses them
            anyway, and hiding the link keeps the sidebar honest. BRAND.md
            prescribes no icon set for navigation (checked 2026-10-28), so the
            links stay text. */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <GroupLabel first>Work</GroupLabel>
          <SidebarLink to="/">Due &amp; overdue</SidebarLink>
          <div className="mt-2 px-3 text-[11px] text-[rgba(255,255,255,.45)]">Obligation types</div>
          <div className="mt-1">
            {/* The name a person reads first, the SARS/CIPC code under it:
                staff know EMP201, nobody should have to decode IRP6_1 or
                COIDA_RETURN. The number is clients carrying the type. */}
            {obligationTypes.map((type) => (
              <SidebarLink key={type.code} to={`/matrix/${type.code}`}>
                <span className="flex w-full items-start justify-between gap-2">
                  <span className="min-w-0">
                    <span className="block leading-snug">{typeLabel(type.description)}</span>
                    <span className="block text-[11px] leading-snug text-[rgba(255,255,255,.45)]">
                      {type.code}
                    </span>
                  </span>
                  <span className="tabular-nums text-[rgba(255,255,255,.5)]">
                    {type.clientCount}
                  </span>
                </span>
              </SidebarLink>
            ))}
          </div>

          <GroupLabel>Clients</GroupLabel>
          {/* Lit on the register and on a client's page, not on Import clients (Admin). */}
          <SidebarLink to="/clients" activeWhen={(path) => path.startsWith('/clients') && !path.startsWith('/clients/import')}>
            Clients
          </SidebarLink>

          {/* Time is per person: signed-in only. Everyone's time, approval and
              rand figures: managers only (decision 5). */}
          {signedIn && (
            <>
              <GroupLabel>Time</GroupLabel>
              <SidebarLink to="/time/my-week">My week</SidebarLink>
              <SidebarLink to="/time/suggestions">Suggestions</SidebarLink>
              {manager && (
                <>
                  <SidebarLink to="/time/approvals">Approvals</SidebarLink>
                  <SidebarLink to="/time/reports">WIP and recovery</SidebarLink>
                  <SidebarLink to="/time/late-timesheets">Late timesheets</SidebarLink>
                  <SidebarLink to="/time/self-approved">Self-approved time</SidebarLink>
                </>
              )}
            </>
          )}

          {/* Anonymous mode (local development) has no roles, so the screens
              that worked there before keep showing; User access needs sign-in. */}
          {(manager || auth.mode === 'anonymous') && (
            <>
              <GroupLabel>Admin</GroupLabel>
              {manager && <SidebarLink to="/users">User access</SidebarLink>}
              <SidebarLink to="/clients/import">Import clients</SidebarLink>
              <SidebarLink to="/time/jobs-and-prices">Jobs and prices</SidebarLink>
            </>
          )}
        </nav>

        <div className="border-t border-[rgba(255,255,255,.08)] px-4 py-3">
          {auth.mode === 'cognito' && auth.user !== null ? (
            <>
              <div className="text-[12.5px] text-[rgba(255,255,255,.6)]">Signed in as</div>
              <div className="truncate text-[12.5px] text-white" title={auth.user.email}>
                {auth.user.name}
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <span className="rounded-eq-pill bg-[rgba(167,199,231,.16)] px-2 py-0.5 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-baby">
                  {auth.user.role === 'manager' ? 'Manager' : 'Staff'}
                </span>
                <a
                  href="/api/auth/logout"
                  className="text-[12.5px] text-[rgba(255,255,255,.6)] underline hover:text-white"
                >
                  Sign out
                </a>
              </div>
            </>
          ) : (
            <>
              {/* Not a placeholder standing in for an account -- this server
                  genuinely has no authentication configured, and saying so is
                  the honest reading of docs/open_questions.md #23. */}
              <div className="text-[12.5px] text-[rgba(255,255,255,.6)]">No sign-in required</div>
              <div className="text-[12.5px] text-white">Anyone with access to this machine</div>
            </>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-b border-eq-border bg-white px-8 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              {eyebrow && (
                <div className="font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral">
                  {eyebrow}
                </div>
              )}
              <h1 className="mt-1 text-[28px] leading-tight">{title}</h1>
              {subtitle && <div className="mt-1 text-[12.5px] text-eq-helper">{subtitle}</div>}
            </div>
            <div className="flex flex-wrap items-start gap-3">
              {/* T&B-1 PR 2: the timer follows the person across every page.
                  It renders nothing without sign-in. */}
              <TimerBar />
              {action}
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-8 py-6">{children}</main>
      </div>
    </div>
  )
}

function GroupLabel({ children, first = false }: { children: ReactNode; first?: boolean }) {
  return (
    <div
      className={`${first ? 'mt-0' : 'mt-5'} mb-1 px-3 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-[rgba(255,255,255,.6)]`}
    >
      {children}
    </div>
  )
}

/**
 * `activeWhen`: when the plain prefix match is wrong (Clients must not light
 * up on /clients/import). Then the link sets aria-current itself, so a screen
 * reader hears one current page, not two.
 */
function SidebarLink({ to, children, activeWhen }: { to: string; children: ReactNode; activeWhen?: (path: string) => boolean }) {
  const { pathname } = useLocation()
  const cls = (active: boolean) =>
    [
      'flex items-center rounded-eq-sm px-3 py-2 text-[14px] no-underline',
      active
        ? 'bg-[rgba(167,199,231,.16)] font-semibold text-white'
        : 'text-[rgba(255,255,255,.6)] hover:bg-[rgba(167,199,231,.08)] hover:text-white',
    ].join(' ')
  if (activeWhen !== undefined) {
    const active = activeWhen(pathname)
    return (
      <Link to={to} aria-current={active ? 'page' : undefined} className={cls(active)}>
        {children}
      </Link>
    )
  }
  return (
    <NavLink to={to} end={to === '/'} className={({ isActive }) => cls(isActive)}>
      {children}
    </NavLink>
  )
}
